import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { RootState } from '../../../app/rootReducer';
import {
  apiEndPoints,
  closeReferralEndPoint,
  createReferralEndPoint,
  getReferralsListEndPoint,
  referralFollowUpEndPoint,
  referralResponseEndPoint,
} from '../../../common/api.endPoints';
import { FlexCol, Select } from '../../../common/components/v2';
import { UserOrganization } from '../../../common/constants/userOrganization';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { HttpService } from '../../../common/http.service';
import { SnackBarType } from '../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../layout/SnackbarContext';
import { AuthContext } from '../../login/AuthWrapper';
import { IFilterItem } from '../interfaces/IFilterItem';
import {
  formatReferralConversationAsText,
  formatReferralStatus,
  getReferralErrorMessage,
  sortReferralMessages,
} from './referralSectionUtils';
import styles from './styles.module.scss';

const accordionSummarySx = {
  padding: 0,
  margin: 0,
  minHeight: '0 !important' as const,
  '& .MuiAccordionSummary-content': { margin: '0 0 0 0 !important' as const },
};

const REFERRAL_REASON_PLACEHOLDER = { value: '', label: 'Select reason' };

const REFERRAL_REASON_BASE_OPTIONS: { value: string; label: string }[] = [
  REFERRAL_REASON_PLACEHOLDER,
  {
    value: 'Referred for medical opinion',
    label: 'Referred for medical opinion',
  },
  {
    value: 'Referred due to case beyond authority limit',
    label: 'Referred due to case beyond authority limit',
  },
  { value: 'Referred for withdrawal', label: 'Referred for withdrawal' },
  {
    value: 'Referred for case / process complexity (non-medical)',
    label: 'Referred for case / process complexity (non-medical)',
  },
  {
    value: 'Agent Quality Assurance Review',
    label: 'Agent Quality Assurance Review',
  },
  { value: 'Other', label: 'Other' },
];

/** Parent reason that reveals a second sub-reason dropdown (Phase 2). */
export const REFERRAL_UWA_PARENT_VALUE = 'Referred to UWA';
/** Sub-reason value that reveals a free-text field under "Referred to UWA". */
export const REFERRAL_UWA_OTHER_VALUE = 'Others';

const REFERRAL_UWA_SUB_PLACEHOLDER = { value: '', label: 'Select sub-reason' };

const REFERRAL_UWA_SUB_OPTIONS: { value: string; label: string }[] = [
  REFERRAL_UWA_SUB_PLACEHOLDER,
  { value: 'TLO Report', label: 'TLO Report' },
  { value: 'ExamOne Criminal Records', label: 'ExamOne Criminal Records' },
  {
    value: 'Incorrect evidence received',
    label: 'Incorrect evidence received',
  },
  { value: 'Manual Letters', label: 'Manual Letters' },
  { value: 'Document upload', label: 'Document upload' },
  { value: REFERRAL_UWA_OTHER_VALUE, label: 'Others (Free text)' },
];

/**
 * Full Phase 2 reason list (explicit order). Exported for consumers that need
 * all options (e.g. display of existing saved reasons). The five former flat
 * UWA items and "Criminal history" are intentionally not top-level here — they
 * now live under "Referred to UWA" as sub-reasons.
 */
export const REFERRAL_REASON_OPTIONS: { value: string; label: string }[] = [
  REFERRAL_REASON_PLACEHOLDER,
  {
    value: 'Referred due to case beyond authority limit',
    label: 'Referred due to case beyond authority limit',
  },
  { value: 'Referred for withdrawal', label: 'Referred for withdrawal' },
  { value: 'Referred for Peer Review', label: 'Referred for Peer Review' },
  { value: REFERRAL_UWA_PARENT_VALUE, label: 'Referred to UWA' },
  {
    value: 'Referred for Foreign Nationals',
    label: 'Referred for Foreign Nationals',
  },
  {
    value: 'Referred for medical opinion',
    label: 'Referred for medical opinion',
  },
  {
    value: 'Referred for case / process complexity (non-medical)',
    label: 'Referred for case / process complexity (non-medical)',
  },
  {
    value: 'Referred for Manual Criminal Records',
    label: 'Referred for Manual Criminal Records',
  },
  {
    value: 'Agent Quality Assurance Review',
    label: 'Agent Quality Assurance Review',
  },
  {
    value: 'Referred for Ownership Change request (UWA to UW Manager)',
    label: 'Referred for Ownership Change request (UWA to UW Manager)',
  },
  {
    value: 'Referred for Underwriting related queries (UWA to UW)',
    label: 'Referred for Underwriting related queries (UWA to UW)',
  },
  { value: 'Strategy Rules review', label: 'Strategy Rules review' },
  { value: 'Other', label: 'Other' },
];

const REFERRAL_HISTORY_LOADING = 'Loading history...';
const REFERRAL_HISTORY_EMPTY = 'No closed referrals for this case.';

export interface ReferralMessage {
  /** Normalized from API's referralMessageId for consistent use (keys, etc.). */
  id: number;
  userId: number;
  userDisplayName: string;
  isQuestion: boolean;
  messageText: string;
  createdAt: string | null;
}

export interface ReferralData {
  id: number;
  /** From API as referralId; normalized to id when loading */
  referralId?: number;
  applicationId: number;
  referredFromUserId: number;
  referredFromUserDisplayName: string;
  referredToUserId: number;
  referredToUserDisplayName: string;
  status: string;
  reason: string | null;
  messages: ReferralMessage[];
  createdAt: string;
  updatedAt: string;
}

/** API returns referralId and message.referralMessageId; normalize to id for consistent use. */
function normalizeReferralData(
  body: ReferralData & { referralId?: number },
): ReferralData {
  const id = body.referralId ?? body.id;
  const messages = (body.messages ?? []).map(
    (msg: ReferralMessage & { referralMessageId?: number }) => {
      const { referralMessageId: msgId, ...rest } = msg;
      return { ...rest, id: msgId ?? rest.id };
    },
  );
  return { ...body, id, messages };
}

export const CaseReferralSection = ({
  applicationId,
}: {
  applicationId: string;
}) => {
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [referralUsers, setReferralUsers] = useState<IFilterItem[]>([]);
  const [referralUsersLoadError, setReferralUsersLoadError] = useState<
    string | null
  >(null);
  const [selectedReferTo, setSelectedReferTo] = useState<IFilterItem | null>(
    null,
  );
  const [selectedReason, setSelectedReason] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [otherReasonText, setOtherReasonText] = useState('');
  const [selectedUwaSubReason, setSelectedUwaSubReason] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [uwaOtherText, setUwaOtherText] = useState('');
  const [initialQuestion, setInitialQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [followUpText, setFollowUpText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [submittingFollowUp, setSubmittingFollowUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [referralHistory, setReferralHistory] = useState<ReferralData[]>([]);
  const [expandedHistoryId, setExpandedHistoryId] = useState<number | null>(
    null,
  );
  const { openSnacks, setSnacks } = useContext(SnackbarContext);
  const auth = useContext(AuthContext);
  const { flags } = useContext(FeatureFlagsContext);
  const isPhase2 = flags.isCaseReferralPhase2Enabled;
  const referralReasonOptions = isPhase2
    ? REFERRAL_REASON_OPTIONS
    : REFERRAL_REASON_BASE_OPTIONS;
  const underwriterId = useSelector(
    (state: RootState) => state.application.underwriterId,
  );
  const caseManagerId = useSelector(
    (state: RootState) => state.application.caseManagerId,
  );

  const [activeRef] = referralHistory.filter(r => r.status !== 'closed');
  const activeReferral = activeRef ? sortReferralMessages(activeRef) : null;

  const isEthosUser =
    (auth?.userOrganization as UserOrganization) === UserOrganization.ETHOS;

  const canCreateReferral =
    (underwriterId > 0 && auth?.id === underwriterId) ||
    (caseManagerId > 0 && auth?.id === caseManagerId);

  const canCloseReferral =
    !!activeReferral && activeReferral.referredFromUserId === auth?.id;

  const canRespond =
    !!activeReferral && activeReferral.referredToUserId === auth?.id;

  const canFollowUp =
    !!activeReferral && activeReferral.referredFromUserId === auth?.id;

  const showSuccessSnack = (message: string, idPrefix: string) => {
    setSnacks([
      ...openSnacks,
      { id: `${idPrefix}-${Date.now()}`, type: SnackBarType.SUCCESS, message },
    ]);
  };

  const fetchReferrals = useCallback(async () => {
    setError(null);
    try {
      const response = await HttpService.getInstance().get(
        getReferralsListEndPoint(applicationId),
      );
      const list = response?.parsedBody;
      const items = Array.isArray(list)
        ? (list as (ReferralData & { referralId?: number })[]).map(
            normalizeReferralData,
          )
        : [];
      setReferralHistory(items);
    } catch {
      setReferralHistory([]);
      setError('Failed to load referrals.');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const closedReferrals = useMemo(
    () => referralHistory.filter(r => r.status === 'closed'),
    [referralHistory],
  );

  const closedReferralMessagesByKey = useMemo(() => {
    const map = new Map<number, ReferralMessage[]>();
    closedReferrals.forEach(ref => {
      map.set(ref.id, sortReferralMessages(ref).messages ?? []);
    });
    return map;
  }, [closedReferrals]);

  const loadReferralUsers = useCallback(async () => {
    const fallbackUsers: IFilterItem[] = [{ value: -1, label: 'Select user' }];
    setReferralUsersLoadError(null);
    try {
      const data = await HttpService.getInstance().get(apiEndPoints.referrals);
      const raw = data?.parsedBody;
      const items = Array.isArray(raw)
        ? (raw as { id: number; displayName: string }[])
        : [];
      const currentUserId = auth?.id ?? -1;
      const users: IFilterItem[] = [
        { value: -1, label: 'Select user' },
        ...items
          .filter((item: { id: number }) => item.id !== currentUserId)
          .map((item: { id: number; displayName: string }) => ({
            value: item.id,
            label: item.displayName ?? String(item.id),
          })),
      ];
      setReferralUsers(users);
    } catch {
      setReferralUsers(fallbackUsers);
      setReferralUsersLoadError(
        'Could not load user list. Close and reopen to try again.',
      );
    }
  }, [auth?.id]);

  useEffect(() => {
    if (createModalOpen) {
      loadReferralUsers();
      setSelectedReferTo({ value: -1, label: 'Select user' });
      setSelectedReason(REFERRAL_REASON_PLACEHOLDER);
      setOtherReasonText('');
      setSelectedUwaSubReason(null);
      setUwaOtherText('');
      setInitialQuestion('');
      setError(null);
      setReferralUsersLoadError(null);
    }
  }, [createModalOpen, loadReferralUsers]);

  const handleCreateSubmit = async () => {
    if (!selectedReferTo || selectedReferTo.value < 1) {
      setError('Please select a user to refer to.');
      return;
    }
    if (!selectedReason || !selectedReason.value) {
      setError('Please select a reason.');
      return;
    }
    const isOtherReason = selectedReason.value === 'Other';
    if (isOtherReason && !otherReasonText.trim()) {
      setError('Please specify the reason for referral.');
      return;
    }
    const isUwaReason = selectedReason.value === REFERRAL_UWA_PARENT_VALUE;
    const isUwaOtherSubReason =
      selectedUwaSubReason?.value === REFERRAL_UWA_OTHER_VALUE;
    if (isUwaReason) {
      if (!selectedUwaSubReason || !selectedUwaSubReason.value) {
        setError('Please select a sub-reason for the UWA referral.');
        return;
      }
      if (isUwaOtherSubReason && !uwaOtherText.trim()) {
        setError('Please specify the reason for referral.');
        return;
      }
    }
    if (!initialQuestion.trim()) {
      setError('Please enter an initial question.');
      return;
    }
    let reasonToSend = selectedReason.value;
    if (isOtherReason) {
      reasonToSend = otherReasonText.trim();
    } else if (isUwaReason && selectedUwaSubReason) {
      const subReason = isUwaOtherSubReason
        ? uwaOtherText.trim()
        : selectedUwaSubReason.value;
      reasonToSend = `${REFERRAL_UWA_PARENT_VALUE} - ${subReason}`;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await HttpService.getInstance().post(
        createReferralEndPoint(applicationId),
        {
          referredToUserId: selectedReferTo.value,
          reason: reasonToSend,
          initialQuestion: initialQuestion.trim(),
        },
      );
      if (response?.error) {
        setError(
          getReferralErrorMessage(
            response.error,
            'Failed to create referral.',
            { forCreate: true },
          ),
        );
        return;
      }
      setCreateModalOpen(false);
      showSuccessSnack('Referral created.', 'referral-created');
      fetchReferrals();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to create referral.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseReferral = async () => {
    if (!activeReferral || closing) return;
    setClosing(true);
    setError(null);
    try {
      const referralId = activeReferral.referralId ?? activeReferral.id;
      const response = await HttpService.getInstance().patch(
        closeReferralEndPoint(applicationId, referralId),
      );
      if (response?.error) {
        setError(
          getReferralErrorMessage(response.error, 'Failed to close referral.'),
        );
        return;
      }
      showSuccessSnack('Referral closed.', 'referral-closed');
      fetchReferrals();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to close referral.',
      );
    } finally {
      setClosing(false);
    }
  };

  const handleReply = async () => {
    if (!activeReferral || !replyText.trim() || submittingReply) return;
    setSubmittingReply(true);
    setError(null);
    try {
      const referralId = activeReferral.referralId ?? activeReferral.id;
      const response = await HttpService.getInstance().post(
        referralResponseEndPoint(applicationId, referralId),
        { messageText: replyText.trim() },
      );
      if (response?.error) {
        setError(
          getReferralErrorMessage(response.error, 'Failed to send reply.'),
        );
        return;
      }
      setReplyText('');
      fetchReferrals();
      showSuccessSnack('Reply sent.', 'referral-reply');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reply.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleFollowUp = async () => {
    if (!activeReferral || !followUpText.trim() || submittingFollowUp) return;
    setSubmittingFollowUp(true);
    setError(null);
    try {
      const referralId = activeReferral.referralId ?? activeReferral.id;
      const response = await HttpService.getInstance().post(
        referralFollowUpEndPoint(applicationId, referralId),
        { messageText: followUpText.trim() },
      );
      if (response?.error) {
        setError(
          getReferralErrorMessage(response.error, 'Failed to send follow-up.'),
        );
        return;
      }
      setFollowUpText('');
      showSuccessSnack('Follow-up sent.', 'referral-followup');
      fetchReferrals();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Failed to send follow-up.',
      );
    } finally {
      setSubmittingFollowUp(false);
    }
  };

  if (loading) {
    return (
      <div
        id="case-referral"
        data-section-id="case-referral"
        role="region"
        aria-label="Referral"
      >
        <Accordion sx={{ boxShadow: 'none', p: 0, m: 0 }} expanded={false}>
          <AccordionSummary sx={accordionSummarySx}>
            <Box display="flex" flexDirection="row" alignItems="center">
              <Box left={'-30px'} position="absolute">
                <IconButton size="small">
                  <FontAwesomeIcon icon={faAngleRight} />
                </IconButton>
              </Box>
              <Typography variant="h2">Referral</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <p className={styles.muted}>Loading...</p>
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }

  return (
    <div
      id="case-referral"
      data-section-id="case-referral"
      role="region"
      aria-label="Referral"
    >
      <Accordion sx={{ boxShadow: 'none', p: 0, m: 0 }} expanded={isExpanded}>
        <AccordionSummary
          sx={accordionSummarySx}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box left={'-30px'} position="absolute">
              <IconButton size="small">
                <FontAwesomeIcon
                  icon={faAngleRight}
                  rotation={isExpanded ? 90 : undefined}
                />
              </IconButton>
            </Box>
            <Typography variant="h2">
              Referral
              {activeReferral && (
                <span className={styles.activeLabel}> · Active referral</span>
              )}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <div className={styles.section}>
            <div className={styles.historySection}>
              <Typography variant="subtitle1" className={styles.historyHeading}>
                Referral history
              </Typography>
              {loading ? (
                <p className={styles.muted}>{REFERRAL_HISTORY_LOADING}</p>
              ) : closedReferrals.length === 0 ? (
                <p className={styles.muted}>{REFERRAL_HISTORY_EMPTY}</p>
              ) : (
                <div className={styles.historyList}>
                  {closedReferrals.map(ref => {
                    const messages =
                      closedReferralMessagesByKey.get(ref.id) ?? [];
                    return (
                      <Accordion
                        key={ref.id}
                        disableGutters
                        expanded={expandedHistoryId === ref.id}
                        onChange={() =>
                          setExpandedHistoryId(prev =>
                            prev === ref.id ? null : ref.id,
                          )
                        }
                        sx={{
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            minHeight: '40px !important',
                            cursor: 'pointer',
                            '& .MuiAccordionSummary-content': {
                              margin: '8px 0 !important',
                            },
                          }}
                        >
                          <span className={styles.historySummary}>
                            {new Date(ref.createdAt).toLocaleDateString()} ·{' '}
                            {ref.referredFromUserDisplayName} →{' '}
                            {ref.referredToUserDisplayName}
                            {ref.reason && ` · ${ref.reason}`}
                            <span className={styles.historyExpandHint}>
                              {' '}
                              · Click to view conversation
                            </span>
                          </span>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              mb: 1,
                            }}
                          >
                            <Button
                              size="small"
                              startIcon={<ContentCopyIcon />}
                              data-testid="copy-conversation"
                              onClick={async e => {
                                e.stopPropagation();
                                const text =
                                  formatReferralConversationAsText(ref);
                                try {
                                  await navigator.clipboard.writeText(text);
                                  showSuccessSnack(
                                    'Conversation copied to clipboard.',
                                    'referral-copy',
                                  );
                                } catch {
                                  setSnacks([
                                    ...openSnacks,
                                    {
                                      id: `referral-copy-err-${Date.now()}`,
                                      type: SnackBarType.ALERT,
                                      message: 'Could not copy to clipboard.',
                                    },
                                  ]);
                                }
                              }}
                            >
                              Copy conversation
                            </Button>
                          </Box>
                          {messages.length === 0 ? (
                            <p className={styles.muted}>
                              No messages in this referral.
                            </p>
                          ) : (
                            <ul className={styles.messageList}>
                              {messages.map(msg => (
                                <li
                                  key={msg.id}
                                  className={`${styles.messageItem} ${
                                    msg.isQuestion
                                      ? styles.messageItemQuestion
                                      : styles.messageItemAnswer
                                  } ${styles.messageItemReadOnly}`}
                                >
                                  <span className={styles.messageMeta}>
                                    {msg.userDisplayName} ·{' '}
                                    {msg.createdAt
                                      ? new Date(msg.createdAt).toLocaleString()
                                      : '—'}
                                  </span>
                                  <p className={styles.messageText}>
                                    {msg.messageText}
                                  </p>
                                </li>
                              ))}
                            </ul>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </div>
              )}
            </div>
            {activeReferral ? (
              <>
                <div className={styles.thread}>
                  <div className={styles.threadSummary}>
                    {activeReferral.status && (
                      <div className={styles.threadStatus}>
                        {formatReferralStatus(activeReferral.status)}
                      </div>
                    )}
                    {activeReferral.reason && (
                      <Typography
                        variant="body2"
                        component="p"
                        className={styles.referralReason}
                        sx={{ mb: 1 }}
                      >
                        <span className={styles.referralReasonLabel}>
                          Reason
                        </span>{' '}
                        {activeReferral.reason}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      className={styles.referredFromTo}
                      sx={{ mb: 0 }}
                    >
                      <span className={styles.referredFromToLabel}>
                        Referred from
                      </span>{' '}
                      <strong>
                        {activeReferral.referredFromUserDisplayName}
                      </strong>{' '}
                      <span className={styles.referredFromToLabel}>to</span>{' '}
                      <strong>
                        {activeReferral.referredToUserDisplayName}
                      </strong>
                    </Typography>
                  </div>
                  <ul className={styles.messageList}>
                    {(activeReferral.messages ?? []).map(msg => (
                      <li
                        key={msg.id}
                        className={`${styles.messageItem} ${
                          msg.isQuestion
                            ? styles.messageItemQuestion
                            : styles.messageItemAnswer
                        }`}
                      >
                        <span className={styles.messageMeta}>
                          {msg.isQuestion ? 'Question' : 'Answer'} ·{' '}
                          {msg.userDisplayName} ·{' '}
                          {msg.createdAt
                            ? new Date(msg.createdAt).toLocaleString()
                            : '—'}
                        </span>
                        <p className={styles.messageText}>
                          {msg.messageText ?? ''}
                        </p>
                      </li>
                    ))}
                  </ul>
                  {isEthosUser && canRespond && (
                    <div className={styles.replyBlock}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        placeholder="Add your reply..."
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        size="small"
                        margin="dense"
                        variant="outlined"
                        disabled={submittingReply}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        className={styles.replyButton}
                        onClick={handleReply}
                        disabled={!replyText.trim() || submittingReply}
                      >
                        {submittingReply ? 'Sending...' : 'Send Response'}
                      </Button>
                    </div>
                  )}
                  {isEthosUser && canFollowUp && (
                    <div className={styles.replyBlock}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        placeholder="Add a follow-up question..."
                        value={followUpText}
                        onChange={e => setFollowUpText(e.target.value)}
                        size="small"
                        margin="dense"
                        variant="outlined"
                        disabled={submittingFollowUp}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        className={styles.replyButton}
                        onClick={handleFollowUp}
                        disabled={!followUpText.trim() || submittingFollowUp}
                      >
                        {submittingFollowUp ? 'Sending...' : 'Send follow-up'}
                      </Button>
                    </div>
                  )}
                  {error && (
                    <p className={styles.error} role="alert">
                      {error}
                    </p>
                  )}
                </div>
                {isEthosUser && canCloseReferral && (
                  <div className={styles.closeReferralWrap}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      className={styles.closeReferralButton}
                      onClick={handleCloseReferral}
                      disabled={closing}
                    >
                      {closing ? 'Closing...' : 'Close Referral Thread'}
                    </Button>
                  </div>
                )}
              </>
            ) : !isEthosUser ? (
              <div className={styles.emptyState}>
                <p className={styles.emptyStateMessage}>No active referral.</p>
              </div>
            ) : !canCreateReferral ? (
              <div className={styles.emptyState}>
                <p className={styles.emptyStateMessage}>
                  Only the assigned underwriter or case manager can create a
                  referral.
                </p>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p className={styles.emptyStateMessage}>
                  No active referral. Use the button below to refer this case to
                  another underwriter or case manager.
                </p>
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  className={styles.emptyStateButton}
                  onClick={() => setCreateModalOpen(true)}
                >
                  Create referral
                </Button>
              </div>
            )}
          </div>
        </AccordionDetails>
      </Accordion>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={createModalOpen}
        onClose={() => !submitting && setCreateModalOpen(false)}
      >
        <DialogTitle>Create referral</DialogTitle>
        <DialogContent>
          {referralUsersLoadError && (
            <p className={styles.error} role="alert">
              {referralUsersLoadError}
            </p>
          )}
          <FlexCol className={styles.formField}>
            <Select
              className={styles.referToSelect}
              name="Refer to"
              value={selectedReferTo ?? { value: -1, label: 'Select user' }}
              options={referralUsers}
              onChange={item => setSelectedReferTo(item as IFilterItem)}
              title="Refer to"
              placeholder="Select user"
            />
          </FlexCol>
          <FlexCol className={styles.formField}>
            <Select
              className={styles.referToSelect}
              name="Reason"
              value={selectedReason ?? REFERRAL_REASON_PLACEHOLDER}
              options={referralReasonOptions}
              onChange={item => {
                setSelectedReason(item as { value: string; label: string });
                setSelectedUwaSubReason(null);
                setUwaOtherText('');
              }}
              title="Reason *"
              placeholder="Select reason"
            />
          </FlexCol>
          {selectedReason?.value === REFERRAL_UWA_PARENT_VALUE && (
            <FlexCol className={styles.formField}>
              <Select
                className={styles.referToSelect}
                name="Sub-reason"
                value={selectedUwaSubReason ?? REFERRAL_UWA_SUB_PLACEHOLDER}
                options={REFERRAL_UWA_SUB_OPTIONS}
                onChange={item =>
                  setSelectedUwaSubReason(
                    item as { value: string; label: string },
                  )
                }
                title="Sub-reason *"
                placeholder="Select sub-reason"
              />
            </FlexCol>
          )}
          {selectedReason?.value === REFERRAL_UWA_PARENT_VALUE &&
            selectedUwaSubReason?.value === REFERRAL_UWA_OTHER_VALUE && (
              <FlexCol className={styles.formField}>
                <TextField
                  variant="standard"
                  label="Please specify reason"
                  value={uwaOtherText}
                  onChange={e => setUwaOtherText(e.target.value)}
                  fullWidth
                  required
                  size="small"
                  margin="dense"
                  placeholder="Enter the reason for referral"
                />
              </FlexCol>
            )}
          {selectedReason?.value === 'Other' && (
            <FlexCol className={styles.formField}>
              <TextField
                variant="standard"
                label="Please specify reason"
                value={otherReasonText}
                onChange={e => setOtherReasonText(e.target.value)}
                fullWidth
                required
                size="small"
                margin="dense"
                placeholder="Enter the reason for referral"
              />
            </FlexCol>
          )}
          {selectedReason?.value && (
            <FlexCol className={styles.formField}>
              <TextField
                label="Underwriter Observations (Private)"
                value={initialQuestion}
                onChange={e => setInitialQuestion(e.target.value)}
                fullWidth
                multiline
                rows={3}
                required
                size="small"
                margin="dense"
                placeholder="Enter the initial question or context for the referral"
              />
            </FlexCol>
          )}
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateModalOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create referral'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
